import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { CHAMADOS_CONFIG_DESIGN as D } from './ChamadosConfig.design';
import ModalConfimacao from 'src/components/modal/ModalConfimacao';
import {
  actionCriarCaixaEntrada,
  actionDeletarCaixaEntrada,
} from 'src/actions/chamados/actionChamadosConfig';
import { fDate } from 'src/utils/formatTime';
import Iconify from 'src/components/Iconify';

export default function CaixasEntradaTab({
  listaCaixas,
  onRefresh,
  clienteId,
  createRequest,
  onCreateRequestHandled,
}) {
  const navigate = useNavigate();
  const { t } = useTranslation('settings');
  const [openConfirmacao, setOpenConfirmacao] = useState(false);
  const [loading, setLoading] = useState(false);
  const [caixaId, setCaixaId] = useState('');

  async function handleCriar() {
    try {
      setLoading(true);
      const response = await actionCriarCaixaEntrada({
        nome: t('ticketSettings.inbox.defaultName', 'Nova Caixa de Entrada'),
        cliente_id: clienteId,
      });
      const novaCaixa = response.data.data;
      navigate(`/dashboard/configuracoes/chamados-config-builder/${novaCaixa._id}`);
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    if (!createRequest) return;
    onCreateRequestHandled?.();
    handleCriar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [createRequest]);

  async function handleDeletar() {
    try {
      setLoading(true);
      await actionDeletarCaixaEntrada(caixaId);
      await onRefresh();
      setOpenConfirmacao(false);
      setCaixaId('');
    } catch (error) {
      console.log(error);
    } finally {
      setLoading(false);
    }
  }

  return (
    <div className={D.stack}>
      <ModalConfimacao
        open={openConfirmacao}
        onClose={() => setOpenConfirmacao(false)}
        onConfirm={handleDeletar}
        loadingFinalizar={loading}
        mensagem={t('ticketSettings.inbox.deleteConfirmMessage', 'Deseja mesmo excluir essa caixa de entrada?')}
      />

      <div className={D.sectionHeader}>
        <div className={D.sectionHeaderMain}>
          <h2 className={D.sectionHeaderTitle}>
            {t('ticketSettings.inbox.sectionTitle', 'Caixas de entrada')}
          </h2>
          <p className={D.sectionHeaderSub}>
            {t('ticketSettings.inbox.sectionSub', 'Clique em uma caixa para editar status, prioridades e visibilidade.')}
          </p>
        </div>
      </div>

      <div className={D.cardFlush}>
        {listaCaixas.length === 0 ? (
          <div className={D.emptyWrap}>
            <div className={D.emptyIcon}>
              <Iconify icon="eva:inbox-outline" width={28} />
            </div>
            <p className={D.emptyTitle}>{t('ticketSettings.inbox.emptyTitle', 'Nenhuma caixa de entrada')}</p>
            <p className={D.emptyDesc}>
              {t('ticketSettings.inbox.emptyDescription', 'Crie caixas de entrada para organizar chamados externos.')}
            </p>
            <button type="button" onClick={handleCriar} disabled={loading} className={`${D.link} mt-4`}>
              {t('ticketSettings.inbox.emptyCreateButton', 'Criar minha primeira caixa')}
            </button>
          </div>
        ) : (
          <div className={D.tableWrap}>
            <div className={D.tableHeadRow}>
              <div className={`flex-1 min-w-[180px] pr-4 ${D.tableHead}`}>
                {t('labels.name', { ns: 'common', defaultValue: 'Nome' })}
              </div>
              <div className={`flex-1 pr-4 hidden md:block ${D.tableHead}`}>
                {t('labels.description', { ns: 'common', defaultValue: 'Descrição' })}
              </div>
              <div className={`w-28 text-center ${D.tableHead}`}>
                {t('labels.status', { ns: 'common', defaultValue: 'Status' })}
              </div>
              <div className={`w-28 text-center hidden sm:block ${D.tableHead}`}>
                {t('ticketSettings.inbox.table.published', 'Publicado')}
              </div>
              <div className={`w-36 text-center hidden lg:block ${D.tableHead}`}>
                {t('ticketSettings.inbox.table.visibility', 'Visibilidade')}
              </div>
              <div className={`w-32 text-center hidden xl:block ${D.tableHead}`}>
                {t('labels.createdAt', { ns: 'common', defaultValue: 'Criado em' })}
              </div>
              <div className={`w-16 text-right ${D.tableHead}`}>
                {t('buttons.actions', { ns: 'common', defaultValue: 'Ações' })}
              </div>
            </div>

            {listaCaixas.map((row) => (
              <div
                key={row._id}
                className={D.tableRow}
                onClick={() => navigate(`/dashboard/configuracoes/chamados-config-builder/${row._id}`)}
              >
                <div className={`flex-1 min-w-[180px] pr-4 ${D.tableCell} font-semibold truncate`}>
                  {row.nome}
                </div>
                <div className={`flex-1 pr-4 hidden md:block ${D.tableCellMuted} truncate`}>
                  {row.descricao || '—'}
                </div>
                <div className="w-28 flex justify-center">
                  <span className={`${D.badge} ${row.status === 'active' ? D.badgeOk : D.badgeNeutral}`}>
                    <span className={`${D.badgeDot} ${row.status === 'active' ? D.badgeDotOk : D.badgeDotNeutral}`} />
                    {row.status === 'active'
                      ? t('status.active', { ns: 'common', defaultValue: 'Ativo' })
                      : t('status.draft', { ns: 'common', defaultValue: 'Rascunho' })}
                  </span>
                </div>
                <div className="w-28 hidden sm:flex justify-center">
                  <span className={`${D.badge} ${row.isPublished ? D.badgeOk : D.badgeNeutral}`}>
                    <span className={`${D.badgeDot} ${row.isPublished ? D.badgeDotOk : D.badgeDotNeutral}`} />
                    {row.isPublished
                      ? t('buttons.yes', { ns: 'common', defaultValue: 'Sim' })
                      : t('buttons.no', { ns: 'common', defaultValue: 'Não' })}
                  </span>
                </div>
                <div className="w-36 hidden lg:flex justify-center">
                  <span className={`${D.badge} ${row.visibilidade_cliente ? D.badgeOk : D.badgeNeutral}`}>
                    <span className={`${D.badgeDot} ${row.visibilidade_cliente ? D.badgeDotOk : D.badgeDotNeutral}`} />
                    {row.visibilidade_cliente
                      ? t('ticketSettings.inbox.badge.visible', 'Visível')
                      : t('ticketSettings.inbox.badge.internal', 'Interno')}
                  </span>
                </div>
                <div className={`w-32 text-center hidden xl:block ${D.tableCellMuted}`}>
                  {fDate(row.criado_em)}
                </div>
                <div className="w-16 flex justify-end">
                  <button
                    type="button"
                    title={t('buttons.delete', { ns: 'common', defaultValue: 'Excluir' })}
                    onClick={(e) => {
                      e.stopPropagation();
                      setCaixaId(row._id);
                      setOpenConfirmacao(true);
                    }}
                    className={D.iconButtonDanger}
                  >
                    <Iconify icon="eva:trash-2-outline" width={16} />
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
